import React, { useEffect, useState } from "react";
import useAxios from "axios-hooks";
import {
  RiDownloadCloud2Line,
  RiSearchLine,
  RiFilterLine,
} from "react-icons/ri";
import { useSetRecoilState } from "recoil";
import {
  Box,
  Flex,
  FormLabel,
  Input,
  Select,
  Button,
  FormControl,
  Text,
  Icon,
  InputGroup,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  Tooltip,
  Heading,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { motion } from "framer-motion";
import {
  FaRupeeSign,
  FaBolt,
  FaListAlt,
  FaCalendarAlt,
} from "react-icons/fa";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import ChargingSessionsColumnsTable from "../components/ChargingSessionsColumsTable";
import { chargingSessionTableHeaders } from "../variables/tableHeaders";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const StatsCard = ({ title, value, icon, helpText, color }) => {
  return (
    <MotionBox
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
      maxW={{ base: "100%", md: "320px" }}
      minH={{ base: "120px", md: "145px" }}
      flex="1"
    >
      <GlassMorphismCard p={{ base: 4, md: 6 }}>
        <Flex justify="space-between" align="center">
          <Stat>
            <StatLabel color="gray.500" fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">
              {title}
            </StatLabel>
            <StatNumber
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="bold"
              color={color || "gray.700"}
            >
              {value}
            </StatNumber>
            {helpText && (
              <StatHelpText fontSize={{ base: "2xs", md: "xs" }} color="gray.500">
                {helpText}
              </StatHelpText>
            )}
          </Stat>
          <Flex
            justify="center"
            align="center"
            borderRadius="full"
            bg={`${color}20` || "gray.100"}
            w={{ base: "50px", md: "60px" }}
            h={{ base: "50px", md: "60px" }}
          >
            <Icon as={icon} color={color} w={{ base: 5, md: 6 }} h={{ base: 5, md: 6 }} />
          </Flex>
        </Flex>
      </GlassMorphismCard>
    </MotionBox>
  );
};

const ChargingSessions = ({ id }) => {
  const [sessions, setSessions] = useState([]);
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState({});
  const setToast = useSetRecoilState(toastState);
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  const [{ data, loading, error }, execute] = useAxios(
    { method: "GET", url: `/api/chargingSession/forStation?stationId=${id}` },
    { manual: true }
  );

  const [
    { data: statsData, loading: statsLoading, error: statsError },
    getSessionStatsData,
  ] = useAxios(
    { method: "GET", url: `/api/chargingSession/stats/forStation` },
    { manual: true }
  );

  const [
    { data: exportData, loading: exportLoading, error: exportError },
    exportSessionsData,
  ] = useAxios(
    { method: "GET", url: `/api/chargingSession/download/forStation` },
    { manual: true }
  );

  const [{ sourceData, sourceLoading, sourceError }, getAllSources] = useAxios(
    {
      method: "GET",
      url: `/api/source/all`,
    },
    { manual: true }
  );

  const {
    control,
    formState: { errors, isValid },
    getValues,
  } = useForm({
    mode: "onChange",
  });

  const options = sources.map((ele, index) => {
    return <option key={index}>{ele.Name}</option>;
  }) || <div></div>;

  const getSources = async () => {
    try {
      const response = await getAllSources();
      setSources(response.data.data);
      setToast({
        status: "success",
        message: "Sources fetched successfully",
        title: "Success",
        position: "top-right",
        duration: 5000,
      });
    } catch (error) {
      logger.log("error occured");
      setToast({
        title: "Error fetching sources",
        status: "error",
        duration: 5000,
        iseOpen: true,
      });
    }
  };

  const getStats = async () => {
    try {
      const formValues = getValues();
      let params = {};
      params["stationId"] = id;
      params["from"] =
        formValues.From !== "" ? formValues.From : formatDate(threeMonthsAgo);
      params["to"] = formValues.To !== "" ? formValues.To : getCurrentDate();
      params["source"] = formValues.Source;
      logger.log(params);
      const response = await getSessionStatsData({ params });
      logger.log(response);
      setStats(response.data.data);
      setToast({
        status: "success",
        message: "Stats fetched successfully",
        title: "Success",
        position: "top-right",
        duration: 5000,
      });
    } catch (error) {
      logger.log("error occured");
      setToast({
        title: "Error fetching stats",
        status: "error",
        duration: 5000,
        iseOpen: true,
      });
    }
  };

  const exportHandler = async () => {
    const formValues = getValues();
    let params = {};
    params["stationId"] = id;
    params["from"] =
      formValues.From !== "" ? formValues.From : formatDate(threeMonthsAgo);
    params["to"] = formValues.To !== "" ? formValues.To : getCurrentDate();
    params["source"] = formValues.Source;

    try {
      const response = await exportSessionsData({ params });
      logger.log("response", response);
      // Create a blob object from the response data
      const blob = new Blob([response.data], { type: "application/json" });

      // Create a temporary anchor element to trigger the download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.headers["content-disposition"].split("=")[1]; // Extract the filename from the Content-Disposition header
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      setToast({
        isOpen: true,
        title: "sessions downloaded successfully",
        status: "success",
      });
    } catch (error) {
      logger.error("Error downloading logs:", error);
      setToast({
        isOpen: true,
        title: "Error occured while getting session data",
        status: "error",
      });
    }
  };
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Zero-padding month
    const day = String(today.getDate()).padStart(2, "0"); // Zero-padding day
    const hours = String(today.getHours()).padStart(2, "0"); // Zero-padding hour
    const minutes = String(today.getMinutes()).padStart(2, "0"); // Zero-padding minute
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  function formatDate(date, hours = 0, minutes = 0) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Zero-padding month
    const day = String(date.getDate()).padStart(2, "0"); // Zero-padding day
    const formattedDate = `${year}-${month}-${day}`;
    const formattedTime = `${String(hours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")}`;
    return `${formattedDate}T${formattedTime}`;
  }

  const fetchSessions = async () => {
    const currentDate = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(currentDate.getMonth() - 3);

    const formValues = getValues();
    let params = {};
    params["from"] = formValues.From || formatDate(threeMonthsAgo); // Use three months ago if From is not provided
    params["to"] = formValues.To || getCurrentDate(); // Use getCurrentDate if To is not provided
    params["source"] = formValues.Source;

    try {
      const response = await execute({ params });
      const result = response.data.data;
      console.log(result);
      setSessions(result);
      await getStats();
    } catch (error) {
      logger.log("error occurred");
    }
  };

  useEffect(() => {
    // getSources();
    fetchSessions();
  }, []);
  const themeColor = "#f57d36";
  const inputBgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Search Filters */}
      <GlassMorphismCard mb={{ base: 4, md: 6 }}>
        <Box p={{ base: 4, md: 6 }} borderBottom="1px solid" borderColor={borderColor}>
          <Flex justify="space-between" align="center" mb={{ base: 3, md: 4 }} direction={{ base: "column", sm: "row" }} gap={{ base: 2, sm: 0 }}>
            <Heading size={{ base: "sm", md: "md" }} color="gray.700">
              <Icon as={RiFilterLine} mr={2} color={themeColor} />
              Session Filters
            </Heading>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
              Filter by date range and source
            </Text>
          </Flex>
        </Box>

        <Box p={{ base: 4, md: 6 }}>
          <Grid
            templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }}
            gap={{ base: 3, md: 6 }}
            mb={{ base: 4, md: 6 }}
          >
            {[
              {
                label: "From Date",
                type: "datetime-local",
                name: "From",
                icon: FaCalendarAlt,
                defaultValue: formatDate(threeMonthsAgo),
              },
              {
                label: "To Date",
                type: "datetime-local",
                name: "To",
                icon: FaCalendarAlt,
                defaultValue: getCurrentDate(),
              },
              {
                label: "Origin",
                type: "select",
                name: "Source",
                icon: RiFilterLine,
                defaultValue: "",
              },
            ].map((item) => {
              return (
                <Controller
                  key={item.name}
                  name={item.name}
                  control={control}
                  defaultValue={item.defaultValue}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel
                        fontSize={{ base: "xs", md: "sm" }}
                        fontWeight="medium"
                        color="gray.600"
                      >
                        <Flex align="center">
                          <Icon as={item.icon} mr={2} color={themeColor} boxSize={{ base: 3, md: 4 }} />
                          {item.label}
                        </Flex>
                      </FormLabel>
                      {item.type === "datetime-local" && (
                        <InputGroup>
                          <Input
                            {...field}
                            bg={inputBgColor}
                            borderRadius="md"
                            borderColor={borderColor}
                            focusBorderColor={themeColor}
                            fontSize={{ base: "xs", md: "sm" }}
                            size={{ base: "sm", md: "md" }}
                            type={item.type}
                            boxShadow="sm"
                            _focus={{ boxShadow: `0 0 0 1px ${themeColor}` }}
                            _hover={{ borderColor: themeColor }}
                            cursor="pointer"
                            onClick={(e) => {
                              // When clicking anywhere in the input, open the date picker
                              e.currentTarget.showPicker();
                            }}
                            sx={{
                              // Custom styling to make calendar icon trigger picker too
                              "&::-webkit-calendar-picker-indicator": {
                                cursor: "pointer",
                                opacity: 0.8,
                                transition: "all 0.2s",
                                filter:
                                  "invert(70%) sepia(59%) saturate(2532%) hue-rotate(334deg) brightness(101%) contrast(96%)",
                                "&:hover": {
                                  opacity: 1,
                                  transform: "scale(1.1)",
                                },
                              },
                            }}
                          />
                        </InputGroup>
                      )}
                      {item.type === "select" && (
                        <Select
                          {...field}
                          bg={inputBgColor}
                          borderRadius="md"
                          borderColor={borderColor}
                          focusBorderColor={themeColor}
                          fontSize={{ base: "xs", md: "sm" }}
                          size={{ base: "sm", md: "md" }}
                          boxShadow="sm"
                          _focus={{ boxShadow: `0 0 0 1px ${themeColor}` }}
                          _hover={{ borderColor: themeColor }}
                        >
                          <option value="">All Origins</option>
                          <option value="PARTNER">PARTNER</option>
                          <option value="CMS-UI">CMS-UI</option>
                          <option value="OCPI">OCPI</option>
                          <option value="rfid_authorized">
                            RFID-AUTHORIZED
                          </option>
                          {options}
                        </Select>
                      )}
                    </FormControl>
                  )}
                />
              );
            })}
          </Grid>

          <Flex justify={{ base: "center", md: "flex-end" }} gap={{ base: 2, md: 3 }} direction={{ base: "column", sm: "row" }} w={{ base: "100%", sm: "auto" }}>
            <Button
              leftIcon={<RiSearchLine />}
              bg={themeColor}
              color="white"
              _hover={{ bg: `${themeColor}90`, transform: "translateY(-2px)" }}
              _active={{ bg: `${themeColor}d0` }}
              borderRadius="md"
              onClick={fetchSessions}
              isLoading={loading}
              transition="all 0.2s"
              boxShadow="md"
              w={{ base: "100%", sm: "auto" }}
              fontSize={{ base: "sm", md: "md" }}
              size={{ base: "sm", md: "md" }}
            >
              Search Sessions
            </Button>
            <Tooltip
              label="Export session data to CSV"
              hasArrow
              placement="top"
            >
              <Button
                leftIcon={<RiDownloadCloud2Line />}
                variant="outline"
                borderColor={themeColor}
                color={themeColor}
                _hover={{
                  bg: `${themeColor}10`,
                  transform: "translateY(-2px)",
                }}
                borderRadius="md"
                onClick={exportHandler}
                isLoading={exportLoading}
                transition="all 0.2s"
                w={{ base: "100%", sm: "auto" }}
                fontSize={{ base: "sm", md: "md" }}
                size={{ base: "sm", md: "md" }}
              >
                Export Data
              </Button>
            </Tooltip>
          </Flex>
        </Box>
      </GlassMorphismCard>

      {/* Stats Section */}
      <Box mb={{ base: 4, md: 6 }}>
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 6 }}
          justify="flex-start"
          align="stretch"
        >
          <StatsCard
            title="Total Amount"
            value={
              <Flex align="center">
                <Icon as={FaRupeeSign} mr={1} />{" "}
                {stats.totalAmount?.toFixed(2) || "0.00"}
              </Flex>
            }
            icon={FaRupeeSign}
            color={themeColor}
            helpText="Total revenue from all sessions"
          />
          <StatsCard
            title="Total Units"
            value={
              <Flex align="center">
                {stats.totalUnitsConsumed?.toFixed(2) || "0.00"}
                <Text ml={1} fontSize="lg">
                  kWh
                </Text>
              </Flex>
            }
            icon={FaBolt}
            color="#3182CE"
            helpText="Total energy consumed"
          />
          <StatsCard
            title="Total Sessions"
            value={stats.totalSessions || 0}
            icon={FaListAlt}
            color="#38A169"
            helpText={`${sessions.length} sessions loaded`}
          />
        </Flex>
      </Box>

      {/* Sessions Table */}
      {sessions.length > 0 ? (
        <ChargingSessionsColumnsTable
          columnsData={chargingSessionTableHeaders}
          tableData={sessions}
        />
      ) : (
        <GlassMorphismCard p={0}>
          <MotionFlex
            direction="column"
            align="center"
            justify="center"
            py={{ base: 12, md: 16 }}
            px={{ base: 4, md: 0 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Icon
              as={FaListAlt}
              boxSize={{ base: 10, md: 12 }}
              color={themeColor}
              mb={4}
              opacity={0.6}
            />
            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mb={2}>
              No Sessions Found
            </Text>
            <Text color="gray.500" textAlign="center" maxW="md" fontSize={{ base: "sm", md: "md" }} px={{ base: 4, md: 0 }}>
              There are no charging sessions matching your filter criteria. Try
              changing your filters or select a different date range.
            </Text>
          </MotionFlex>
        </GlassMorphismCard>
      )}
    </MotionBox>
  );
};

export default ChargingSessions;
